/**
 * Session Storage key helper
 *
 * Centralizes the logic for prefixing session storage keys so that different funnels
 * don't collide when accessed in the same browser tab.
 *
 * Behavior:
 * - Uses the `storageKeyName` from the funnel's config.json to prefix keys
 * - For variant-cv-1: keys are prefixed with `sl_variant_cv_`
 * - For variant-cv-nad: keys are prefixed with `sl_variant_cv_nad_`
 * - Backward compatibility: NAD funnel routes also support legacy `nad_` prefix
 *
 * Usage:
 *   import { getSessionKey, sessionStore } from './sessionKey'
 *   sessionStore.getItem('cv_promo_ribbon') // → reads "sl_variant_cv_nad_cv_promo_ribbon" on /nad
 *   sessionStore.setItem('everflow_start_quiz_tracked', 'true')
 *   sessionStore.removeItem('cv_amount_off')
 *
 * Or use getSessionKey directly:
 *   sessionStorage.getItem(getSessionKey('cv_promo_ribbon'))
 */

// Cache for funnel config to avoid repeated lookups
let cachedConfig = null
let cachedStorageKeyName = null

/**
 * Determine the funnel route from the current pathname.
 * @returns {string|null} The funnel route (e.g., '/cv-1', '/nad') or null
 */
const getFunnelRoute = () => {
  if (typeof window === 'undefined' || !window.location) {
    return null
  }
  const pathname = window.location.pathname || ''
  
  // Check for NAD funnel routes
  if (pathname.startsWith('/nad')) {
    return '/nad'
  }
  
  // Check for Sermorelin funnel routes
  if (pathname.startsWith('/sermorelin')) {
    return '/sermorelin'
  }
  
  // Check for CV-3 funnel routes
  if (pathname.startsWith('/cv-3')) {
    return '/cv-3'
  }

  // Check for CV-4 funnel routes
  if (pathname.startsWith('/cv-4')) {
    return '/cv-4'
  }

  // Check for CV-2 funnel routes
  if (pathname.startsWith('/cv-2')) {
    return '/cv-2'
  }

  // Check for CV-1 funnel routes
  if (pathname.startsWith('/cv-1')) {
    return '/cv-1'
  }
  
  // Check for payment return routes
  if (pathname.startsWith('/payment/return/nad')) {
    return '/nad'
  }
  if (pathname.startsWith('/payment/return/sermorelin')) {
    return '/sermorelin'
  }
  if (pathname.startsWith('/payment/return/cv4')) {
    return '/cv-4'
  }
  if (pathname.startsWith('/payment/return/cv3')) {
    return '/cv-3'
  }
  if (pathname.startsWith('/payment/return/cv2')) {
    return '/cv-2'
  }
  if (pathname.startsWith('/payment/return/cv')) {
    return '/cv-1'
  }
  
  return null
}

/**
 * Get the storageKeyName from the funnel config.
 * Attempts to read from the FunnelContext or fallback to route-based detection.
 * @param {Object} config - Optional funnel config object from FunnelContext
 * @returns {string|null} The storage key name (e.g., 'sl_variant_cv', 'sl_variant_cv_nad')
 */
const getStorageKeyName = (config = null) => {
  // If config is provided and has storageKeyName, use it
  if (config?.storageKeyName) {
    cachedConfig = config
    cachedStorageKeyName = config.storageKeyName
    return config.storageKeyName
  }
  
  // If we have a cached value, use it
  if (cachedStorageKeyName) {
    return cachedStorageKeyName
  }
  
  // Fallback: Determine from route
  const route = getFunnelRoute()
  if (route === '/nad') {
    return 'sl_variant_cv_nad'
  }
  if (route === '/sermorelin') {
    return 'sl_variant_cv_sermorelin'
  }
  if (route === '/cv-2') {
    return 'sl_variant_cv'
  }
  if (route === '/cv-3') {
    return 'sl_variant_cv_3'
  }
  if (route === '/cv-4') {
    return 'sl_variant_cv_4'
  }
  if (route === '/cv-1') {
    return 'sl_variant_cv'
  }
  
  return null
}

/**
 * Determine if the current page belongs to the NAD funnel.
 * Kept for backward compatibility with existing code.
 * @deprecated Use getFunnelRoute() instead
 * @returns {boolean} True when the current pathname is part of the NAD funnel.
 */
export const isNadFunnel = () => {
  return getFunnelRoute() === '/nad'
}

/**
 * Prefix the provided session storage key with the funnel-specific prefix.
 * Returns the key unchanged if no prefix is determined.
 *
 * Idempotent: a key that's already prefixed is returned as-is.
 *
 * @param {string} key - Raw session storage key
 * @param {Object} config - Optional funnel config object
 * @returns {string} The (possibly) prefixed key
 */
export const getSessionKey = (key, config = null) => {
  if (!key || typeof key !== 'string') {
    return key
  }
  
  const storageKeyName = getStorageKeyName(config)
  
  // If no storage key name, return as-is (backward compatibility)
  if (!storageKeyName) {
    // Legacy NAD prefix for backward compatibility
    if (isNadFunnel() && !key.startsWith('nad_')) {
      return `nad_${key}`
    }
    return key
  }
  
  const prefix = `${storageKeyName}_`
  
  // If already prefixed, return as-is (idempotent)
  if (key.startsWith(prefix)) {
    return key
  }
  
  // Backward compatibility: If key already has legacy nad_ prefix, use as-is
  if (key.startsWith('nad_') && isNadFunnel()) {
    return key
  }
  
  return `${prefix}${key}`
}

/**
 * Convenience wrappers around `window.sessionStorage` that automatically
 * apply funnel-specific prefixing. SSR-safe: returns null/no-op when the
 * `sessionStorage` API isn't available.
 */
export const sessionStore = {
  /**
   * Get item with funnel-specific prefix
   */
  getItem(key, config = null) {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage.getItem(getSessionKey(key, config))
  },
  
  /**
   * Set item with funnel-specific prefix
   */
  setItem(key, value, config = null) {
    if (typeof window === 'undefined' || !window.sessionStorage) return
    window.sessionStorage.setItem(getSessionKey(key, config), value)
  },
  
  /**
   * Remove item with funnel-specific prefix
   */
  removeItem(key, config = null) {
    if (typeof window === 'undefined' || !window.sessionStorage) return
    window.sessionStorage.removeItem(getSessionKey(key, config))
  },
}

export default sessionStore
